import { useRef } from "react";

/**
 * A monotonic key that increments each time the server's `view` is REPLACED — i.e. once per real
 * game-state frame. Both game stores (`game-store` / `online-store`) swap `view` wholesale ONLY in
 * their `onState` handler; timer / turn / pause / clock messages set other fields and leave `view`'s
 * reference untouched. So the identity of `view` is a reliable "have we heard a new frame?" signal.
 *
 * It exists to give the shared {@link import("../components/Card").Card}'s `commitKey` a per-frame
 * token: a card that COMMITS a move should sound + fire its move AT MOST ONCE per frame, so that a
 * flurry of taps on the same card while the move is still in flight (a weak Iranian connection can
 * hold a move unconfirmed for SECONDS) produces exactly one cue and one send — not one per tap. The
 * latch resets on the next frame (the move landed, or was rejected and the card is tappable again).
 *
 * Mutating the ref during render is the standard React pattern for deriving a value from a prop's
 * identity; it is render-pure (same input → same output) and never schedules a re-render.
 */
export function useFrameKey(view: unknown): number {
  const ref = useRef<{ view: unknown; key: number }>({ view, key: 0 });
  if (ref.current.view !== view) {
    ref.current = { view, key: ref.current.key + 1 };
  }
  return ref.current.key;
}
